import React from "react";
import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useStore } from "store";
import { useWindowDimensions } from "resources/BrowserManager";

import {
  Flex,
  Typography,
  Tabs,
  Space,
  Tooltip,
  Button,
  Alert,
  // Badge,
} from "antd";
const { Title, Text } = Typography;

import { InfoCircleFilled, CrownFilled } from "@ant-design/icons";

import { getFormattedUnixDate } from "shared/utilities";
import { PlanTag } from "../../../components/Shared/RfcCard";
import TableCFDIs from "./TableCFDIs";
import SearchBox from "./SearchBox";
import ModalRfcManualUpdate from "../../../../app/components/Shared/ModalRfcManualUpdate/index";
import ModalRequestCfdi from "routes/Profile/components/ModalRequestCfdi";
import ModalCfdiDetail from "./ModalCfdiDetail";
import ModalUnsupportedPlan from "../../../components/Shared/ModalUnsupportedPlan";
// import ModalCfdiReportDownload from "../../../components/Shared/ModalCfdiReportDownload/index";

import "./style.scss";
const styleWrapperClass = "cfdi";

export const RfcDateLastUpdateLabel = ({
  active_rfc_obj,
  type,
  showInfoPill,
  style,
}) => {
  const { viewSize } = useWindowDimensions();

  const date = (
    <Space>
      <Text italic strong>
        {(type === "cfdiReceived" &&
          active_rfc_obj.date_last_update_received_unix) ||
        (type === "cfdiEmitted" && active_rfc_obj.date_last_update_emitted_unix)
          ? getFormattedUnixDate(
              type === "cfdiReceived"
                ? active_rfc_obj.date_last_update_received_unix
                : active_rfc_obj.date_last_update_emitted_unix,
            )
          : "N/A"}
      </Text>
      {showInfoPill && (
        <Tooltip title="La información se actualiza de acuerdo a tu horario de actualización.">
          <InfoCircleFilled
            key="icon-help"
            className="cursor-help"
            style={{ color: "purple", cursor: "help" }}
          />
        </Tooltip>
      )}
    </Space>
  );

  const dateContainer =
    viewSize === "small" ? (
      <Space direction="vertical" size={0}>
        <Text italic>Última actualización:</Text>
        {date}
      </Space>
    ) : (
      <Space>
        <Text italic>Última actualización:</Text>
        {date}
      </Space>
    );

  return <Space style={style}>{dateContainer}</Space>;
};

const SectionControls = ({
  active_rfc_obj,
  activeTab,
  showDegradedServiceBanner,
}) => {
  const { isSmallView } = useWindowDimensions();

  const DegradedServiceBanner = () => (
    <Alert
      message={
        <Text>
          <span style={{ fontWeight: 600 }}>
            Intermitencias en el servicio:
          </span>
          &nbsp;Por el momento, el portal del SAT presenta intermitencias en el
          servicio. Tus CFDIs se actualizarán a la brevedad.
        </Text>
      }
      type="warning"
      showIcon
    />
  );

  return (
    <Flex vertical gap={isSmallView ? 30 : 20}>
      {showDegradedServiceBanner && <DegradedServiceBanner />}
      <SearchBox rfcObj={active_rfc_obj} activeTab={activeTab} />
      <Flex gap={isSmallView ? 20 : 10}>
        <ModalRfcManualUpdate showIcon useSmallButton type={activeTab} />
        <ModalRequestCfdi showIcon useSmallButton />
        {/* <ModalCfdiReportDownload /> */}
      </Flex>
    </Flex>
  );
};

const CfdiTabs = React.memo(
  ({
    navigateRef,
    active_rfc_obj,
    activeTab,
    efosRfc,
    setCfdiSearchFilters,
    setActiveTab,
    setEfosRfc,
    tableData,
    setTableData,
  }) => {
    const CfdiTabTitle = ({ type }) => {
      if (type === "cfdiReceived") {
        return "Recibidos";
        // return (
        //   <>
        //     Recibidos&nbsp;&nbsp;
        //     <Badge count={4} color="#a5a5a5ff" />
        //   </>
        // );
      }

      if (active_rfc_obj?.plan_name === "pro") {
        return "Emitidos";
      }

      return (
        <>
          Emitidos &nbsp;
          <PlanTag planName={"pro"} cursor={"pointer"} />
        </>
      );
    };

    const CfdiTabContent = ({ type, active_rfc_obj }) => {
      const content = (
        <>
          <RfcDateLastUpdateLabel
            type={type}
            active_rfc_obj={active_rfc_obj}
            showInfoPill={true}
            style={{ marginBottom: "10px" }}
          />
          <TableCFDIs
            cfdiType={type}
            activeTab={activeTab}
            efosRfc={efosRfc}
            tableData={tableData}
            setTableData={setTableData}
          />
        </>
      );

      if (type === "cfdiReceived") {
        return content;
      } else if (type === "cfdiEmitted") {
        if (active_rfc_obj.plan_name === "pro") {
          return content;
        } else {
          return (
            <Space
              style={{
                marginTop: "20px",
                marginBottom: "20px",
                width: "100%",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              <Space direction="vertical">
                <Title level={3}>
                  Disponible en el plan <CrownFilled className="fg-gold" /> Pro
                </Title>
                <Title level={5}>
                  Para ver tus CFDIs emitidos, deberás subscribirte al plan{" "}
                  <CrownFilled className="fg-gold" /> Pro.
                </Title>
                <Button onClick={() => navigateRef.current("/plan")}>
                  Ver Planes
                </Button>
              </Space>
            </Space>
          );
        }
      }
    };

    const tabItems = [
      {
        key: "cfdiReceived",
        label: <CfdiTabTitle type="cfdiReceived" />,
        children: (
          <CfdiTabContent type="cfdiReceived" active_rfc_obj={active_rfc_obj} />
        ),
      },
      {
        key: "cfdiEmitted",
        label: <CfdiTabTitle type="cfdiEmitted" />,
        children: (
          <CfdiTabContent type="cfdiEmitted" active_rfc_obj={active_rfc_obj} />
        ),
      },
    ];

    const handleTabChange = (tab) => {
      setCfdiSearchFilters(null);
      setActiveTab(tab);
      setEfosRfc(null);
      navigateRef.current("/cfdis", { replace: true }); //Reset URL params on tab change
    };

    return (
      <Tabs
        size="large"
        defaultActiveKey={activeTab}
        items={tabItems}
        onTabClick={handleTabChange}
        style={{ marginTop: "20px" }}
      />
    );
  },
);

export default function CFDI({ source }) {
  const navigate = useNavigate();
  const navigateRef = React.useRef(navigate);
  navigateRef.current = navigate;

  const { id_cfdi } = useParams();
  const [searchParams] = useSearchParams();

  const efos_link = searchParams.get("efos_link");
  const efos_rfc = searchParams.get("efos_rfc");

  const tabForEfos = efos_link
    ? efos_link === "emitter"
      ? "cfdiReceived"
      : "cfdiEmitted"
    : null;

  const [activeTab, setActiveTab] = useState(tabForEfos || "cfdiReceived");
  const [efosRfc, setEfosRfc] = useState(efos_rfc);

  const user = useStore((state) => state.user);
  const activeRfc = useStore((state) => state.activeRfc);
  const setCfdiSearchFilters = useStore((state) => state.setCfdiSearchFilters);

  const active_rfc_obj = user.rfcs[activeRfc];

  return (
    <div className={styleWrapperClass}>
      {source === "admin" ? (
        <Title level={5}>CFDIs</Title>
      ) : (
        <Title level={1}>CFDIs</Title>
      )}
      <SectionControls
        active_rfc_obj={active_rfc_obj}
        activeTab={activeTab}
        showDegradedServiceBanner={false}
      />
      <CfdiTabs
        navigateRef={navigateRef}
        active_rfc_obj={active_rfc_obj}
        activeTab={activeTab}
        efosRfc={efosRfc}
        setCfdiSearchFilters={setCfdiSearchFilters}
        setActiveTab={setActiveTab}
        setEfosRfc={setEfosRfc}
      />

      {id_cfdi && !["pro", "basic"].includes(active_rfc_obj.plan_name) ? (
        // Show unsupported modal if user loads CFDI deeplink in free plan
        <ModalUnsupportedPlan
          descriptionTitlePrefix="Para ver los detalles de tus CFDIs"
          requiredPlans={["pro", "basic"]}
          autoShow={true}
        />
      ) : (
        // Load defail modal for deeplinks otherwise
        <ModalCfdiDetail id_cfdi={id_cfdi} cfdiType={activeTab} />
      )}
    </div>
  );
}
